import React, { useEffect, useState } from 'react';
import { useAccount } from 'wagmi';
import { FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { SiTether } from 'react-icons/si';
import { GiReceiveMoney, GiBull, GiBearFace } from 'react-icons/gi';
import { useT } from '../../../i18n';
import { chainId, erc20Address } from '../../../.config';
import { useBullvsBearContract } from '../hooks/useBullvsBearContract';
import { useUSDTBalance } from '../hooks/useUSDTBalance';
import { usePredictions } from '../context/PredictionsContext';
import { estimatePayout, nextMultiplier, formatUsdt } from '../utils/roundMath';

const QUICK_AMOUNTS = [5, 10, 25];
const GET_USDT_URL = `https://quickswap.exchange/#/swap?outputCurrency=${erc20Address}`;

/**
 * Bet on the next round: amount (quick values and MAX), then the side. Each side button carries the whole
 * decision: what it pays per USDT and how much this bet gets back if that side wins (bet included), with the
 * early-bird bonus when there are spots left. Errors only show once the player tries to bet.
 */
const BetPanel = ({ epoch, pool, config, earlyBirdLeft, referralAddress }) => {
  const t = useT();
  const { address } = useAccount();
  const { betUp, betDown } = useBullvsBearContract();
  const { getUSDTBalance } = useUSDTBalance();
  const { fetchContractData } = usePredictions();
  const [amount, setAmount] = useState('10');
  const [balance, setBalance] = useState(null);
  const [pending, setPending] = useState(null);
  const [tried, setTried] = useState(false);

  useEffect(() => {
    let active = true;
    if (address) getUSDTBalance(address).then((b) => active && setBalance(Number(b)));
    else setBalance(null);
    return () => {
      active = false;
    };
    // getUSDTBalance is recreated on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, pending]);

  const value = Number(amount);
  const minBet = config.minBet || 0;
  const tooLow = value > 0 && value < minBet;
  const tooHigh = balance !== null && value > balance;
  const valid = value > 0 && !tooLow && !tooHigh;
  const noFunds = address && balance !== null && balance < Math.max(minBet, 0.01);
  const bonusBps = earlyBirdLeft > 0 ? config.earlyBirdBonusBps : 0;
  const fees = { feeBps: config.feeBps, bonusBps };

  const place = async (side) => {
    setTried(true);
    if (!address || !valid) return;
    setPending(side);
    const referrer = referralAddress || '0x0000000000000000000000000000000000000000';
    const result = side === 'up' ? await betUp(amount, epoch, referrer) : await betDown(amount, epoch, referrer);
    setPending(null);
    setTried(false);
    if (result?.success) fetchContractData();
  };

  const sideButton = (side) => {
    const up = side === 'up';
    const Animal = up ? GiBull : GiBearFace;
    const back = value > 0 ? estimatePayout(pool, side, value, fees) : 0;
    const mult = value > 0 ? back / value : nextMultiplier(pool, side, fees);
    return (
      <button
        key={side}
        onClick={() => place(side)}
        disabled={!!pending || !address}
        title={value > 0 ? t('bvb.multTip', { bet: formatUsdt(value), amount: formatUsdt(back) }) : undefined}
        className={`group relative flex flex-col items-center gap-1 rounded-2xl border-2 px-3 pb-3 pt-4 transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
          up
            ? 'border-emerald-400/50 bg-emerald-500/10 text-emerald-300 hover:border-emerald-300 hover:bg-emerald-500/20'
            : 'border-rose-400/50 bg-rose-500/10 text-rose-300 hover:border-rose-300 hover:bg-rose-500/20'
        }`}
      >
        {bonusBps > 0 && (
          <span className="absolute -top-2.5 right-2 rounded-full bg-amber-400 px-2 py-0.5 text-[10px] font-extrabold text-black">
            {t('bvb.earlyBirdTag', { bonus: bonusBps / 100 })}
          </span>
        )}
        <Animal className="h-11 w-11 transition-transform group-hover:scale-110" aria-hidden="true" />
        <span className="flex items-center gap-1.5 text-lg font-extrabold">
          {up ? <FaArrowUp size={14} /> : <FaArrowDown size={14} />}
          {pending === side ? t('bvb.confirming') : up ? t('bvb.up') : t('bvb.down')}
        </span>
        <span className="lb-num text-sm font-bold text-white">{t('bvb.pays', { mult: mult > 0 ? mult.toFixed(2) : '—' })}</span>
        <span className="lb-num text-[11px] text-white/55">{back > 0 ? t('bvb.youGet', { amount: formatUsdt(back) }) : ' '}</span>
      </button>
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-1.5 flex items-center justify-between text-xs">
          <span className="text-white/50">{t('bvb.betAmount')}</span>
          {balance !== null && (
            <button className="lb-num text-white/50 hover:text-white" onClick={() => setAmount(String(Math.floor(balance * 100) / 100))}>
              {t('bvb.balance')}: {formatUsdt(balance)}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/40 px-3 focus-within:border-[#a369d9]">
          <SiTether className="shrink-0 text-[#26A17B]" />
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="1"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="lb-num w-full min-w-0 bg-transparent py-2.5 text-xl font-bold text-white outline-none"
            aria-label={t('bvb.betAmount')}
          />
          <span className="text-xs text-white/40">USDT</span>
        </div>
        <div className="mt-2 grid grid-cols-4 gap-1.5">
          {QUICK_AMOUNTS.map((q) => (
            <button
              key={q}
              onClick={() => setAmount(String(q))}
              className={`rounded-lg py-1 text-xs font-semibold transition-colors ${
                value === q ? 'bg-brand text-white' : 'bg-white/5 text-white/70 hover:bg-white/10'
              }`}
            >
              {q}
            </button>
          ))}
          <button
            onClick={() => balance !== null && setAmount(String(Math.floor(balance * 100) / 100))}
            disabled={balance === null}
            className="rounded-lg bg-white/5 py-1 text-xs font-semibold text-white/70 hover:bg-white/10 disabled:opacity-40"
          >
            {t('bvb.max')}
          </button>
        </div>
        {tried && !noFunds && (tooLow || tooHigh || !(value > 0)) && (
          <p className="mt-1.5 text-xs text-rose-300">
            {tooLow ? t('bvb.minBet', { amount: formatUsdt(minBet) }) : tooHigh ? t('bvb.insufficientBalance') : t('bvb.enterAmount')}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {sideButton('up')}
        {sideButton('down')}
      </div>
      {['up', 'down'].every((side) => nextMultiplier(pool, side, fees) < 1) && (
        <p className="text-center text-[11px] text-white/45">{t('bvb.lowPayoutHint')}</p>
      )}

      {!address && (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-white/10 bg-black/30 p-3">
          <p className="text-xs text-white/55">{t('bvb.connectToPlay')}</p>
          {/* same connect button as the header (AppKit web component) */}
          <appkit-button />
        </div>
      )}
      {noFunds && (
        <div className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-black/30 px-3 py-2.5">
          <p className="text-xs text-white/65">{t('bvb.needUsdt')}</p>
          {chainId !== 31337 && (
            <a href={GET_USDT_URL} target="_blank" rel="noopener noreferrer" className="lb-btn lb-btn-outline lb-btn-sm shrink-0">
              {t('bvb.getUsdt')}
            </a>
          )}
        </div>
      )}
    </div>
  );
};

/** Claims a won round or the refund of a delayed one */
const ClaimButton = ({ epoch, amount, isRefund = false }) => {
  const t = useT();
  const [isClaiming, setIsClaiming] = useState(false);
  const { claimWinnings } = useBullvsBearContract();
  const { fetchContractData } = usePredictions();

  const claim = async () => {
    setIsClaiming(true);
    const result = await claimWinnings(epoch);
    setIsClaiming(false);
    if (result?.success) fetchContractData();
  };

  return (
    <button
      onClick={claim}
      disabled={isClaiming}
      className={`flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold text-white shadow-lg transition-all disabled:opacity-50 ${
        isRefund ? 'bg-gradient-to-r from-orange-500 to-amber-500' : 'bg-gradient-to-r from-brand to-fuchsia-500'
      }`}
    >
      <GiReceiveMoney className="h-5 w-5" />
      {isClaiming
        ? t('bvb.claiming')
        : t(isRefund ? 'bvb.claimRefundAmount' : 'bvb.collectAmount', { amount: formatUsdt(amount) })}
    </button>
  );
};

export { BetPanel, ClaimButton };
